// Loan Compliance Module v1.0 PR 7 + PR 9 — Track 3 Annual Legal
// Compliance Review workbench. A single shared component that drives both
// loan- and pool-scoped engagements (parameterized via a
// LegalReviewBindings adapter). Hosts: scoping memo editor, module
// panels (3.1–3.9 for loans, 3.10–3.15 for pools), report draft +
// viewer (LLCR / PLCR), pool-only Officer's Certificate Support File
// generator, workflow actions, memo library, and findings.
// Privileged-only; the workbench bails when the API returns a summary
// projection.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, FormField, Input, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import {
  useLoanLegalReviewBindings,
  useSpvPoolLegalReviewBindings,
  type LegalReviewBindings,
  type NormalizedReview,
  type NormalizedReviewModule,
} from "./bindings";
import type {
  LegalReviewConclusion,
  LegalReviewScope,
  LoanLegalReviewStatus,
  ReviewedDocumentInput,
} from "@/api/compliance/legalReviewTypes";
import type { ComplianceSeverity } from "@/api/compliance/types";
import { extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";

const STATUS_BADGE: Record<
  LoanLegalReviewStatus,
  React.ComponentProps<typeof Badge>["status"]
> = {
  Scoping: "draft",
  Fieldwork: "info",
  Draft: "draft",
  InReview: "review",
  Finalized: "finalized",
  Reissued: "archived",
};

interface LegalReviewWorkbenchProps {
  reviewId: string;
  /** Scope + subject id come from the workspace URL. The workbench rejects
   *  loan scope without a loan id and pool scope without a pool id with
   *  the same UX (link back to the queue). */
  scope: LegalReviewScope;
  subjectId: string;
}

export function LegalReviewWorkbench({
  reviewId,
  scope,
  subjectId,
}: LegalReviewWorkbenchProps) {
  if (!subjectId) {
    return (
      <Card>
        <p
          className="text-small text-status-error m-0"
          role="alert"
          data-testid="legal-review-workbench-no-subject"
        >
          Open this engagement from the {scope === "pool" ? "pool" : "loan"}
          &apos;s Legal Review tab so the {scope === "pool" ? "pool" : "loan"}
          context is preserved.
        </p>
        <Link
          to="/compliance?section=legal-reviews"
          className="text-small text-gold-shadow hover:underline mt-space-2 inline-block"
        >
          ← Back to workspace
        </Link>
      </Card>
    );
  }
  return scope === "pool" ? (
    <PoolWorkbench subjectId={subjectId} reviewId={reviewId} />
  ) : (
    <LoanWorkbench subjectId={subjectId} reviewId={reviewId} />
  );
}

function LoanWorkbench({
  subjectId,
  reviewId,
}: {
  subjectId: string;
  reviewId: string;
}) {
  const bindings = useLoanLegalReviewBindings(subjectId, reviewId);
  return <Inner bindings={bindings} />;
}

function PoolWorkbench({
  subjectId,
  reviewId,
}: {
  subjectId: string;
  reviewId: string;
}) {
  const bindings = useSpvPoolLegalReviewBindings(subjectId, reviewId);
  return <Inner bindings={bindings} />;
}

function Inner({ bindings }: { bindings: LegalReviewBindings }) {
  const { reviewQuery, isSummaryOnly } = bindings;

  if (reviewQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          Loading engagement…
        </p>
      </Card>
    );
  }
  if (reviewQuery.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0" role="alert">
          {extractErrorMessage(reviewQuery.error)}
        </p>
      </Card>
    );
  }
  if (isSummaryOnly || !reviewQuery.data) {
    return (
      <Card>
        <p
          className="text-small text-gray-mid m-0"
          data-testid="legal-review-workbench-summary-only"
        >
          You do not have privilege to view the working notes for this
          engagement. Contact compliance oversight (E4) if you need access.
        </p>
      </Card>
    );
  }
  return <Workbench review={reviewQuery.data} bindings={bindings} />;
}

function Workbench({
  review,
  bindings,
}: {
  review: NormalizedReview;
  bindings: LegalReviewBindings;
}) {
  const locked =
    review.status === "Finalized" || review.status === "Reissued";

  const allConcluded =
    review.modules.length > 0 &&
    review.modules.every((m) => m.conclusion !== "Pending");

  return (
    <div
      className="flex flex-col gap-space-4"
      data-testid="legal-review-workbench"
      data-scope={bindings.scope}
    >
      <Header review={review} bindings={bindings} locked={locked} />
      <PrivilegeBanner />

      <ScopingMemoCard review={review} bindings={bindings} locked={locked} />

      <section
        aria-label="Module conclusions"
        className="flex flex-col gap-space-3"
      >
        <h2 className="font-display font-bold text-h3 m-0">
          Module conclusions ({bindings.moduleRangeLabel})
        </h2>
        {bindings.modulesCatalog.map((m) => {
          const moduleRow =
            review.modules.find((row) => row.moduleCode === m.code) ?? null;
          return (
            <ModulePanel
              key={m.code}
              moduleCode={m.code}
              moduleName={m.name}
              row={moduleRow}
              review={review}
              bindings={bindings}
              locked={locked}
            />
          );
        })}
      </section>

      <ReportCard review={review} bindings={bindings} locked={locked} />

      {bindings.supportsOfficerCertSupport && (
        <OfficerCertSupportCard
          review={review}
          bindings={bindings}
          locked={locked}
        />
      )}

      <WorkflowActions
        review={review}
        bindings={bindings}
        locked={locked}
        allConcluded={allConcluded}
      />

      <MemosSection bindings={bindings} review={review} locked={locked} />

      <FindingsSection review={review} />
    </div>
  );
}

function Header({
  review,
  bindings,
  locked,
}: {
  review: NormalizedReview;
  bindings: LegalReviewBindings;
  locked: boolean;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-space-3">
        <div>
          <div className="text-caption text-gray-mid">
            <Link
              to="/compliance?section=legal-reviews"
              className="hover:underline"
              data-testid="legal-review-back-to-queue"
            >
              ← Back to queue
            </Link>
          </div>
          <h1 className="font-display font-bold text-h2 m-0 mt-space-1">
            Audit year {review.auditYear}
          </h1>
          <p className="text-caption text-gray-mid mt-space-1 m-0">
            <span data-testid="legal-review-scope-chip">
              <Badge status={bindings.scope === "pool" ? "info" : "draft"}>
                {bindings.scope === "pool" ? "Pool" : "Loan"}
              </Badge>
            </span>{" "}
            <Link
              to={bindings.subjectDetailPath}
              className="text-gold-shadow hover:underline"
              data-testid="legal-review-subject-link"
            >
              {bindings.subjectBreadcrumbLabel} {bindings.subjectId}
            </Link>{" "}
            · attorney {review.attorneyUserId}
            {review.supervisorUserId && (
              <> · supervisor {review.supervisorUserId}</>
            )}
            {review.reissueOfId && <> · reissue of prior version</>}
          </p>
        </div>
        <div className="flex items-center gap-space-2">
          <Badge status={STATUS_BADGE[review.status]}>{review.status}</Badge>
          {locked && (
            <Badge status="finalized" data-testid="legal-review-locked-badge">
              Locked
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
}

function PrivilegeBanner() {
  return (
    <div
      className="border border-gold/40 bg-gold/5 rounded-md p-space-3 text-caption text-onyx"
      role="status"
      data-testid="legal-review-privilege-banner"
    >
      <strong className="font-display uppercase tracking-wide text-gold-shadow">
        Privileged — attorney work product
      </strong>{" "}
      The contents of this engagement (working notes, scoping memo, signed
      memos) are restricted to A3 / E4 under the attorney–client and
      work-product privilege boundary.
    </div>
  );
}

function ScopingMemoCard({
  review,
  bindings,
  locked,
}: {
  review: NormalizedReview;
  bindings: LegalReviewBindings;
  locked: boolean;
}) {
  const editable = !locked && review.status === "Scoping";
  const [memo, setMemo] = useState(review.scopingMemo ?? "");
  const [error, setError] = useState<string | null>(null);
  const mutation = bindings.updateScopingMemo;
  const toast = useToast();

  useEffect(() => {
    setMemo(review.scopingMemo ?? "");
  }, [review.scopingMemo]);

  return (
    <Card>
      <div className="flex items-center justify-between gap-space-3">
        <h2 className="font-display font-bold text-h3 m-0">Scoping memo</h2>
        {editable && (
          <Button
            size="sm"
            loading={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate(
                { scopingMemo: memo },
                {
                  onSuccess: () => toast.success("Scoping memo updated."),
                  onError: (err) => setError(extractErrorMessage(err)),
                },
              );
            }}
            data-testid="legal-review-scoping-save"
          >
            Save memo
          </Button>
        )}
      </div>
      <div className="mt-space-3">
        {editable ? (
          <Textarea
            rows={6}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="Scope of the engagement, areas of focus, exclusions."
            data-testid="legal-review-scoping-input"
          />
        ) : (
          <p className="text-small text-onyx whitespace-pre-wrap m-0">
            {review.scopingMemo || (
              <span className="italic text-gray-mid">
                No scoping memo recorded.
              </span>
            )}
          </p>
        )}
      </div>
      {error && (
        <p
          className="text-caption text-status-error mt-space-2"
          role="alert"
          data-testid="legal-review-scoping-error"
        >
          {error}
        </p>
      )}
    </Card>
  );
}

function ModulePanel({
  moduleCode,
  moduleName,
  row,
  review,
  bindings,
  locked,
}: {
  moduleCode: string;
  moduleName: string;
  row: NormalizedReviewModule | null;
  review: NormalizedReview;
  bindings: LegalReviewBindings;
  locked: boolean;
}) {
  const editable = !locked && review.status !== "Finalized";
  const mutation = bindings.recordModuleConclusion;
  const toast = useToast();
  const { dateTime } = useDateFormatter();

  const [workingNotes, setWorkingNotes] = useState(row?.workingNotes ?? "");
  const [conclusion, setConclusion] = useState<LegalReviewConclusion>(
    row?.conclusion ?? "Pending",
  );
  const [conclusionNote, setConclusionNote] = useState(row?.conclusionNote ?? "");
  const [reviewedDocs, setReviewedDocs] = useState<ReviewedDocumentInput[]>([]);
  const [docId, setDocId] = useState("");
  const [docVersion, setDocVersion] = useState("");
  const [exceptionSeverity, setExceptionSeverity] =
    useState<ComplianceSeverity>("Material");
  const [exceptionTitle, setExceptionTitle] = useState("");
  const [exceptionDescription, setExceptionDescription] = useState("");
  const [programCitation, setProgramCitation] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setWorkingNotes(row?.workingNotes ?? "");
    setConclusion(row?.conclusion ?? "Pending");
    setConclusionNote(row?.conclusionNote ?? "");
  }, [row?.workingNotes, row?.conclusion, row?.conclusionNote]);

  const addDoc = () => {
    if (!docId.trim() || !docVersion.trim()) return;
    setReviewedDocs((prev) => [
      ...prev,
      { documentId: docId.trim(), documentVersion: docVersion.trim() },
    ]);
    setDocId("");
    setDocVersion("");
  };

  const submit = () => {
    setError(null);
    if (conclusion === "ExceptionRaised") {
      if (!exceptionTitle.trim()) {
        setError("Exception title is required.");
        return;
      }
      if (!exceptionDescription.trim()) {
        setError("Exception description is required.");
        return;
      }
    }
    mutation.mutate(
      {
        moduleCode,
        conclusion,
        conclusionNote: conclusionNote.trim() || null,
        workingNotes: workingNotes.trim() || null,
        reviewedDocuments: reviewedDocs.length > 0 ? reviewedDocs : undefined,
        exceptionSeverity:
          conclusion === "ExceptionRaised" ? exceptionSeverity : null,
        exceptionTitle:
          conclusion === "ExceptionRaised" ? exceptionTitle.trim() : null,
        exceptionDescription:
          conclusion === "ExceptionRaised"
            ? exceptionDescription.trim()
            : null,
        programDocumentCitation:
          conclusion === "ExceptionRaised" && programCitation.trim()
            ? programCitation.trim()
            : null,
      },
      {
        onSuccess: () => {
          toast.success("Module conclusion recorded.");
          setReviewedDocs([]);
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Card
      data-testid={`legal-review-module-${moduleCode}`}
      className="border-l-[3px] border-l-gold"
    >
      <header className="flex items-center justify-between gap-space-3">
        <div>
          <h3 className="font-display font-bold text-small m-0">
            <span className="font-mono mr-space-2">{moduleCode}</span>
            {moduleName}
          </h3>
        </div>
        <ConclusionBadge conclusion={row?.conclusion ?? "Pending"} />
      </header>

      <div className="mt-space-3">
        <FormField label="Working notes">
          <Textarea
            rows={3}
            value={workingNotes}
            onChange={(e) => setWorkingNotes(e.target.value)}
            disabled={!editable}
            data-testid={`legal-review-module-${moduleCode}-notes`}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 mt-space-3">
        <FormField label="Conclusion" required>
          <select
            className="border border-border-light rounded-md h-10 px-space-2 text-small"
            value={conclusion}
            onChange={(e) =>
              setConclusion(e.target.value as LegalReviewConclusion)
            }
            disabled={!editable}
            data-testid={`legal-review-module-${moduleCode}-conclusion`}
          >
            <option value="Pending">Pending</option>
            <option value="Compliant">Compliant</option>
            <option value="ExceptionRaised">Exception raised</option>
            <option value="NotApplicable">Not applicable</option>
          </select>
        </FormField>
        <FormField label="Conclusion note">
          <Input
            value={conclusionNote}
            onChange={(e) => setConclusionNote(e.target.value)}
            disabled={!editable}
            data-testid={`legal-review-module-${moduleCode}-conclusion-note`}
          />
        </FormField>
      </div>

      <div className="mt-space-3">
        <FormField label="Documents reviewed (this conclusion)">
          <div className="flex flex-wrap gap-space-2">
            <Input
              value={docId}
              onChange={(e) => setDocId(e.target.value)}
              placeholder="document id"
              disabled={!editable}
              data-testid={`legal-review-module-${moduleCode}-doc-id`}
            />
            <Input
              value={docVersion}
              onChange={(e) => setDocVersion(e.target.value)}
              placeholder="version"
              disabled={!editable}
              data-testid={`legal-review-module-${moduleCode}-doc-version`}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={addDoc}
              disabled={!editable}
              data-testid={`legal-review-module-${moduleCode}-doc-add`}
            >
              Add
            </Button>
          </div>
          {reviewedDocs.length > 0 && (
            <ul
              className="mt-space-2 text-caption text-gray-mid"
              data-testid={`legal-review-module-${moduleCode}-doc-list`}
            >
              {reviewedDocs.map((d, i) => (
                <li key={`${d.documentId}-${i}`}>
                  {d.documentId} · v{d.documentVersion}
                </li>
              ))}
            </ul>
          )}
        </FormField>
      </div>

      {conclusion === "ExceptionRaised" && (
        <div
          className="mt-space-3 border border-status-warning/40 bg-status-warning/5 rounded-md p-space-3"
          data-testid={`legal-review-module-${moduleCode}-exception-fields`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <FormField label="Severity" required>
              <select
                className="border border-border-light rounded-md h-10 px-space-2 text-small"
                value={exceptionSeverity}
                onChange={(e) =>
                  setExceptionSeverity(e.target.value as ComplianceSeverity)
                }
                disabled={!editable}
              >
                <option value="Critical">Critical</option>
                <option value="Material">Material</option>
                <option value="Process">Process</option>
                <option value="Observation">Observation</option>
              </select>
            </FormField>
            <FormField label="Title" required>
              <Input
                value={exceptionTitle}
                onChange={(e) => setExceptionTitle(e.target.value)}
                disabled={!editable}
                data-testid={`legal-review-module-${moduleCode}-exception-title`}
              />
            </FormField>
          </div>
          <FormField
            label="Description"
            required
            className="mt-space-3"
          >
            <Textarea
              rows={3}
              value={exceptionDescription}
              onChange={(e) => setExceptionDescription(e.target.value)}
              disabled={!editable}
              data-testid={`legal-review-module-${moduleCode}-exception-description`}
            />
          </FormField>
          <FormField label="Program document citation" className="mt-space-3">
            <Input
              value={programCitation}
              onChange={(e) => setProgramCitation(e.target.value)}
              placeholder="e.g. SPA § 3.4(a), Indenture § 5.1"
              disabled={!editable}
            />
          </FormField>
        </div>
      )}

      {editable && (
        <div className="mt-space-3 flex justify-end">
          <Button
            size="sm"
            loading={mutation.isPending}
            onClick={submit}
            data-testid={`legal-review-module-${moduleCode}-save`}
          >
            Record conclusion
          </Button>
        </div>
      )}
      {error && (
        <p
          className="text-caption text-status-error mt-space-2"
          role="alert"
          data-testid={`legal-review-module-${moduleCode}-error`}
        >
          {error}
        </p>
      )}
      {row?.completedAt && (
        <p className="text-caption text-gray-mid mt-space-2">
          Last recorded {dateTime(row.completedAt)} by{" "}
          {row.completedByUserId ?? "—"}
        </p>
      )}
    </Card>
  );
}

function ConclusionBadge({ conclusion }: { conclusion: LegalReviewConclusion }) {
  const tone =
    conclusion === "Compliant"
      ? "finalized"
      : conclusion === "ExceptionRaised"
        ? "error"
        : conclusion === "NotApplicable"
          ? "archived"
          : "draft";
  const label =
    conclusion === "ExceptionRaised"
      ? "Exception raised"
      : conclusion === "NotApplicable"
        ? "Not applicable"
        : conclusion;
  return <Badge status={tone}>{label}</Badge>;
}

function ReportCard({
  review,
  bindings,
  locked,
}: {
  review: NormalizedReview;
  bindings: LegalReviewBindings;
  locked: boolean;
}) {
  const mutation = bindings.generateDraft;
  const toast = useToast();
  const canGenerate =
    !locked &&
    (review.status === "Fieldwork" ||
      review.status === "Draft" ||
      review.status === "InReview");

  return (
    <Card>
      <div className="flex items-center justify-between gap-space-3">
        <div>
          <h2 className="font-display font-bold text-h3 m-0">
            {bindings.reportLongName}
          </h2>
          <p className="text-caption text-gray-mid mt-space-1 m-0">
            {bindings.reportDescription}
          </p>
        </div>
        <div className="flex items-center gap-space-2">
          {review.finalReportSubmissionId && (
            <Link
              to={`/reports/${review.finalReportSubmissionId}`}
              data-testid="legal-review-report-link"
            >
              <Button variant="secondary" size="sm">
                Open {bindings.reportShortName}
              </Button>
            </Link>
          )}
          {canGenerate && (
            <Button
              size="sm"
              loading={mutation.isPending}
              onClick={() =>
                mutation.mutate(undefined as unknown as never, {
                  onSuccess: () =>
                    toast.success(`${bindings.reportShortName} draft generated.`),
                  onError: (err) => toast.error(extractErrorMessage(err)),
                })
              }
              data-testid="legal-review-report-generate"
            >
              {review.finalReportSubmissionId
                ? "Regenerate draft"
                : `Generate draft ${bindings.reportShortName}`}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function OfficerCertSupportCard({
  review,
  bindings,
  locked,
}: {
  review: NormalizedReview;
  bindings: LegalReviewBindings;
  locked: boolean;
}) {
  const mutation = bindings.generateOfficerCertSupport;
  const toast = useToast();
  if (!mutation) return null;
  const canGenerate =
    !locked &&
    (review.status === "Fieldwork" ||
      review.status === "Draft" ||
      review.status === "InReview");

  return (
    <Card data-testid="legal-review-officer-cert-support">
      <div className="flex items-center justify-between gap-space-3">
        <div>
          <h2 className="font-display font-bold text-h3 m-0">
            Officer&apos;s Certificate Support File
          </h2>
          <p className="text-caption text-gray-mid mt-space-1 m-0">
            Cross-references the Indenture officer-certification line items
            against the PLCR module conclusions and open Track 3 exceptions.
          </p>
        </div>
        <div className="flex items-center gap-space-2">
          {review.officerCertSupportReportSubmissionId && (
            <Link
              to={`/reports/${review.officerCertSupportReportSubmissionId}`}
              data-testid="legal-review-officer-cert-link"
            >
              <Button variant="secondary" size="sm">
                Open Officer&apos;s Cert Support
              </Button>
            </Link>
          )}
          {canGenerate && (
            <Button
              size="sm"
              loading={mutation.isPending}
              onClick={() =>
                mutation.mutate(undefined as unknown as never, {
                  onSuccess: () =>
                    toast.success("Officer's Certificate Support generated."),
                  onError: (err) => toast.error(extractErrorMessage(err)),
                })
              }
              data-testid="legal-review-officer-cert-generate"
            >
              {review.officerCertSupportReportSubmissionId
                ? "Regenerate support file"
                : "Generate Officer's Cert Support"}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function WorkflowActions({
  review,
  bindings,
  locked,
  allConcluded,
}: {
  review: NormalizedReview;
  bindings: LegalReviewBindings;
  locked: boolean;
  allConcluded: boolean;
}) {
  const auth = useAuth();
  const toast = useToast();
  const submit = bindings.submitForReview;
  const accept = bindings.recordSupervisorAcceptance;
  const finalize = bindings.finalize;
  const reissue = bindings.reissue;

  const canSubmit =
    !locked &&
    (review.status === "Fieldwork" || review.status === "Draft") &&
    auth.hasPermission("Compliance.ConductLegalReview");
  const canAccept =
    !locked &&
    review.status === "InReview" &&
    auth.hasPermission("Compliance.AcceptLegalReview");
  const canFinalize =
    !locked && auth.hasPermission("Compliance.FinalizeLegalReview");
  const canReissue =
    review.status === "Finalized" &&
    auth.hasPermission("Compliance.FinalizeLegalReview");

  const [supervisorUserId, setSupervisorUserId] = useState("");
  const [reissueReason, setReissueReason] = useState("");
  const [reissueOpen, setReissueOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);

  return (
    <Card data-testid="legal-review-workflow">
      <h2 className="font-display font-bold text-h3 m-0">Workflow</h2>
      <p className="text-caption text-gray-mid mt-space-1 m-0">
        {locked
          ? "This engagement is locked. Use Reissue to create a new version."
          : allConcluded
            ? "Every module is concluded — the engagement is ready to finalize."
            : "Finalize is disabled until every module is concluded."}
      </p>

      <div className="mt-space-3 flex flex-wrap items-end gap-space-3">
        <Button
          variant="secondary"
          size="sm"
          disabled={!canSubmit}
          loading={submit.isPending}
          onClick={() =>
            submit.mutate(undefined as unknown as never, {
              onSuccess: () => toast.success("Submitted for supervisor review."),
              onError: (err) => toast.error(extractErrorMessage(err)),
            })
          }
          data-testid="legal-review-action-submit"
        >
          Submit for review
        </Button>

        <div className="flex items-end gap-space-2">
          <FormField label="Supervisor user id">
            <Input
              value={supervisorUserId}
              onChange={(e) => setSupervisorUserId(e.target.value)}
              disabled={!canAccept}
              data-testid="legal-review-action-supervisor-id"
            />
          </FormField>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canAccept || !supervisorUserId.trim()}
            loading={accept.isPending}
            onClick={() =>
              accept.mutate(
                { supervisorUserId: supervisorUserId.trim() },
                {
                  onSuccess: () =>
                    toast.success("Supervisor acceptance recorded."),
                  onError: (err) => toast.error(extractErrorMessage(err)),
                },
              )
            }
            data-testid="legal-review-action-accept"
          >
            Record supervisor acceptance
          </Button>
        </div>

        <Button
          size="sm"
          disabled={!canFinalize || !allConcluded || locked}
          onClick={() => setFinalizeOpen(true)}
          title={
            !allConcluded
              ? "Finalize is disabled until every module is concluded."
              : undefined
          }
          data-testid="legal-review-action-finalize"
        >
          Finalize (sign-off)
        </Button>

        <Button
          variant="secondary"
          size="sm"
          disabled={!canReissue}
          onClick={() => setReissueOpen(true)}
          data-testid="legal-review-action-reissue"
        >
          Reissue
        </Button>
      </div>

      {finalizeOpen && (
        <FinalizeModal
          reportShortName={bindings.reportShortName}
          onClose={() => setFinalizeOpen(false)}
          onConfirm={() =>
            finalize.mutate(
              { certificationDefinitionId: bindings.certificationDefinitionId },
              {
                onSuccess: () => {
                  toast.success(
                    `${bindings.reportShortName} finalized. Workbench is now locked.`,
                  );
                  setFinalizeOpen(false);
                },
                onError: (err) => toast.error(extractErrorMessage(err)),
              },
            )
          }
          pending={finalize.isPending}
        />
      )}

      {reissueOpen && (
        <Modal
          open
          onClose={() => setReissueOpen(false)}
          dismissOnBackdropClick={false}
          title="Reissue engagement"
          description={`Creates a new version with the same ${bindings.scope} + audit year.`}
          size="sm"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setReissueOpen(false)}
                disabled={reissue.isPending}
              >
                Cancel
              </Button>
              <Button
                disabled={reissueReason.trim() === ""}
                loading={reissue.isPending}
                onClick={() =>
                  reissue.mutate(
                    { reason: reissueReason.trim() },
                    {
                      onSuccess: () => {
                        toast.success("Engagement reissued.");
                        setReissueOpen(false);
                      },
                      onError: (err) => toast.error(extractErrorMessage(err)),
                    },
                  )
                }
                data-testid="legal-review-action-reissue-confirm"
              >
                Reissue
              </Button>
            </>
          }
        >
          <FormField label="Reason" required>
            <Textarea
              rows={3}
              value={reissueReason}
              onChange={(e) => setReissueReason(e.target.value)}
              data-testid="legal-review-action-reissue-reason"
            />
          </FormField>
        </Modal>
      )}
    </Card>
  );
}

function FinalizeModal({
  reportShortName,
  onClose,
  onConfirm,
  pending,
}: {
  reportShortName: string;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={`Finalize ${reportShortName} sign-off`}
      description="Reuses the Certifications module sign-off primitive."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            loading={pending}
            onClick={onConfirm}
            data-testid="legal-review-finalize-confirm"
          >
            Sign and finalize
          </Button>
        </>
      }
    >
      <p className="text-small text-onyx m-0">
        Signing finalizes the {reportShortName} as the attorney attestation.
        Once finalized the engagement becomes read-only and a
        CertificationRecord is created against the engagement subject. Use
        Reissue to create a new version if a finding is later identified.
      </p>
    </Modal>
  );
}

function MemosSection({
  bindings,
  review,
  locked,
}: {
  bindings: LegalReviewBindings;
  review: NormalizedReview;
  locked: boolean;
}) {
  const memosQuery = bindings.memosQuery;
  const createMutation = bindings.createMemo;
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [relatedEventType, setRelatedEventType] = useState("");
  const [relatedEventId, setRelatedEventId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canCreate = !locked && review.status !== "Finalized";

  const submit = () => {
    setError(null);
    if (!subject.trim() || !body.trim()) {
      setError("Subject and body are required.");
      return;
    }
    createMutation.mutate(
      {
        subject: subject.trim(),
        body: body.trim(),
        relatedEventType: relatedEventType.trim() || null,
        relatedEventId: relatedEventId.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success("Memo created.");
          setOpen(false);
          setSubject("");
          setBody("");
          setRelatedEventType("");
          setRelatedEventId("");
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Card data-testid="legal-review-memos">
      <div className="flex items-center justify-between gap-space-3">
        <h2 className="font-display font-bold text-h3 m-0">
          Event-triggered memos
        </h2>
        {canCreate && (
          <Button size="sm" onClick={() => setOpen(true)} data-testid="legal-review-memo-create">
            New memo
          </Button>
        )}
      </div>

      {memosQuery.isLoading ? (
        <p className="text-caption text-gray-mid italic mt-space-2 m-0">
          Loading memos…
        </p>
      ) : (memosQuery.data ?? []).length === 0 ? (
        <p
          className="text-caption text-gray-mid italic mt-space-2 m-0"
          data-testid="legal-review-memos-empty"
        >
          No memos.
        </p>
      ) : (
        <ul className="mt-space-3 divide-y divide-border-light border border-border-light rounded-md">
          {memosQuery.data!.map((m) => (
            <MemoRow key={m.id} memo={m} bindings={bindings} />
          ))}
        </ul>
      )}

      {open && (
        <Modal
          open
          onClose={() => setOpen(false)}
          dismissOnBackdropClick={false}
          title="Create memo"
          size="md"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setOpen(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                loading={createMutation.isPending}
                onClick={submit}
                data-testid="legal-review-memo-submit"
              >
                Create
              </Button>
            </>
          }
        >
          <FormField label="Subject" required>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="legal-review-memo-subject"
            />
          </FormField>
          <FormField label="Body" required className="mt-space-3">
            <Textarea
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="legal-review-memo-body"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-space-3 mt-space-3">
            <FormField label="Triggering event type">
              <Input
                value={relatedEventType}
                onChange={(e) => setRelatedEventType(e.target.value)}
                placeholder="MaterialEvent / LoanEvent / …"
              />
            </FormField>
            <FormField label="Triggering event id">
              <Input
                value={relatedEventId}
                onChange={(e) => setRelatedEventId(e.target.value)}
              />
            </FormField>
          </div>
          {error && (
            <p
              className="text-caption text-status-error mt-space-2"
              role="alert"
              data-testid="legal-review-memo-error"
            >
              {error}
            </p>
          )}
        </Modal>
      )}
    </Card>
  );
}

function MemoRow({
  memo,
  bindings,
}: {
  memo: {
    id: string;
    subject: string;
    body: string;
    signedAt: string | null;
    relatedEventType: string | null;
    relatedEventId: string | null;
    attorneyUserId: string;
    createdAt: string;
  };
  bindings: LegalReviewBindings;
}) {
  const toast = useToast();
  const sign = bindings.useSignMemoMutation(memo.id);
  const { dateTime } = useDateFormatter();
  return (
    <li
      className="px-space-3 py-space-3 text-small"
      data-testid={`legal-review-memo-${memo.id}`}
    >
      <div className="flex items-center justify-between gap-space-3">
        <div className="font-semibold">{memo.subject}</div>
        <div className="flex items-center gap-space-2">
          {memo.signedAt ? (
            <Badge status="finalized">Signed</Badge>
          ) : (
            <Badge status="draft">Unsigned</Badge>
          )}
          {!memo.signedAt && (
            <Button
              size="sm"
              variant="secondary"
              loading={sign.isPending}
              onClick={() =>
                sign.mutate(
                  {
                    certificationDefinitionId: bindings.certificationDefinitionId,
                  },
                  {
                    onSuccess: () => toast.success("Memo signed."),
                    onError: (err) => toast.error(extractErrorMessage(err)),
                  },
                )
              }
              data-testid={`legal-review-memo-${memo.id}-sign`}
            >
              Sign
            </Button>
          )}
        </div>
      </div>
      <p className="mt-space-2 text-onyx whitespace-pre-wrap m-0">{memo.body}</p>
      <div className="mt-space-2 text-caption text-gray-mid">
        {memo.attorneyUserId} · {dateTime(memo.createdAt)}
        {memo.relatedEventType && (
          <>
            {" "}
            · trigger {memo.relatedEventType}
            {memo.relatedEventId ? ` (${memo.relatedEventId})` : ""}
          </>
        )}
      </div>
    </li>
  );
}

function FindingsSection({ review }: { review: NormalizedReview }) {
  const findings = useMemo(
    () => review.modules.filter((m) => m.conclusion === "ExceptionRaised"),
    [review.modules],
  );

  return (
    <Card data-testid="legal-review-findings">
      <h2 className="font-display font-bold text-h3 m-0">Findings</h2>
      {findings.length === 0 ? (
        <p
          className="text-caption text-gray-mid italic mt-space-2 m-0"
          data-testid="legal-review-findings-empty"
        >
          No exceptions raised on this engagement.
        </p>
      ) : (
        <ul className="mt-space-2 divide-y divide-border-light border border-border-light rounded-md">
          {findings.map((m) => (
            <li
              key={m.id}
              className="px-space-3 py-space-2 text-small"
              data-testid={`legal-review-finding-${m.moduleCode}`}
            >
              <div className="flex items-center gap-space-2">
                <span className="font-mono">{m.moduleCode}</span>
                <span className="font-semibold">{m.moduleName}</span>
                <Badge status="error">Exception</Badge>
              </div>
              {m.conclusionNote && (
                <p className="text-caption text-gray-mid mt-space-1 m-0">
                  {m.conclusionNote}
                </p>
              )}
              {m.relatedComplianceExceptionId && (
                <Link
                  to={`/compliance/exceptions/${m.relatedComplianceExceptionId}`}
                  className="text-caption text-gold-shadow hover:underline mt-space-1 inline-block"
                >
                  View exception
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
